import axios from 'axios';
import {
  Device,
  DeviceStats,
  RegisterDeviceRequest,
  UpdateDeviceRequest,
  ApiResponse,
  Version,
  CreateVersionRequest,
  UpdateVersionRequest,
  Policy,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  User,
  CreateUserRequest,
  UpdateUserRequest
} from '../types';

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    console.log('API Request:', config.method?.toUpperCase(), config.url, config.data);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data || error.message);

    // 如果是401错误，清除token并重新加载页面
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

export const deviceAPI = {
  // 获取设备统计信息
  getStats: async (): Promise<DeviceStats> => {
    const response = await api.get('/devices/stats');
    return response.data.stats;
  },

  // 获取所有设备
  getDevices: async (): Promise<Device[]> => {
    const response = await api.get('/devices');
    return response.data.devices;
  },

  // 注册新设备
  registerDevice: async (data: RegisterDeviceRequest): Promise<Device> => {
    const response = await api.post('/devices/register', data);
    return response.data.device;
  },

  // 更新设备
  updateDevice: async (data: UpdateDeviceRequest): Promise<Device> => {
    const response = await api.put(`/devices/${data.id}`, data);
    return response.data.device;
  },

  // 删除设备
  deleteDevice: async (id: number): Promise<void> => {
    await api.delete(`/devices/${id}`);
  },

  // 批量导入相关API
  downloadTemplate: (): void => {
    const token = localStorage.getItem('auth_token');
    const url = `${api.defaults.baseURL}/devices/import/template`;

    // 对于文件下载，我们需要使用fetch
    if (token) {
      fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('下载失败');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'device_import_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('下载模板失败:', error);
        throw error;
      });
    }
  },

  importDevices: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/devices/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 批量移动设备到指定目录
  batchMoveDevices: async (deviceIds: number[], targetCategoryId: number, sourceCategoryId?: number): Promise<{
    movedCount: number;
    skippedCount: number;
    message: string;
  }> => {
    const response = await api.post('/devices/batch-move', {
      deviceIds,
      targetCategoryId,
      sourceCategoryId
    });
    return response.data;
  }
};

// 版本管理API
export const versionAPI = {
  // 获取所有版本
  getVersions: async (): Promise<Version[]> => {
    const response = await api.get('/versions');
    return response.data.versions;
  },

  // 创建版本
  createVersion: async (data: CreateVersionRequest): Promise<Version> => {
    const response = await api.post('/versions', data);
    return response.data.version;
  },

  // 更新版本
  updateVersion: async (data: UpdateVersionRequest): Promise<Version> => {
    const response = await api.put(`/versions/${data.id}`, data);
    return response.data.version;
  },

  // 删除版本
  deleteVersion: async (id: number): Promise<void> => {
    await api.delete(`/versions/${id}`);
  },
};

// OSS 上传 API
export const ossAPI = {
  // 上传 APK 到 OSS
  uploadApk: async (file: File, versionCode: number): Promise<{
    url: string;
    ossPath: string;
    size: number;
    originalName: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('versionCode', versionCode.toString());

    const response = await api.post('/oss/upload-apk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5分钟超时（大文件上传）
    });
    return response.data.data;
  },
};

// 策略管理API
export const policyAPI = {
  // 获取所有策略
  getPolicies: async (): Promise<Policy[]> => {
    const response = await api.get('/policies');
    return response.data.policies;
  },

  // 创建策略
  createPolicy: async (data: CreatePolicyRequest): Promise<Policy> => {
    const response = await api.post('/policies', data);
    return response.data.policy;
  },

  // 更新策略
  updatePolicy: async (data: UpdatePolicyRequest): Promise<Policy> => {
    const response = await api.put(`/policies/${data.id}`, data);
    return response.data.policy;
  },

  // 删除策略
  deletePolicy: async (id: number): Promise<void> => {
    await api.delete(`/policies/${id}`);
  },
};

// 目录管理API
export const categoryAPI = {
  // 获取所有目录（树状结构）
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    return response.data.categories;
  },

  // 获取目录树（含设备数量）
  getCategoryTree: async (): Promise<Category[]> => {
    const response = await api.get('/categories/tree');
    return response.data.categories;
  },

  // 获取指定目录下的设备列表
  getCategoryDevices: async (categoryId: number, options?: { includeChildren?: boolean }): Promise<{
    devices: Device[];
    stats: {
      total: number;
      online: number;
      recent: number;
      offline: number;
      disabled: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (options?.includeChildren) {
      params.append('includeChildren', 'true');
    }
    const response = await api.get(`/categories/${categoryId}/devices?${params.toString()}`);
    return { devices: response.data.devices, stats: response.data.stats };
  },

  // 创建目录
  createCategory: async (data: CreateCategoryRequest): Promise<Category> => {
    const response = await api.post('/categories', data);
    return response.data.category;
  },

  // 更新目录
  updateCategory: async (data: UpdateCategoryRequest): Promise<Category> => {
    const response = await api.put(`/categories/${data.id}`, data);
    return response.data.category;
  },

  // 删除目录
  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/categories/${id}`);
  },
};

// 设备专用API（兼容现有的移动端API）
export const deviceServiceAPI = {
  // 检查设备版本
  checkVersion: async (deviceCode: string) => {
    const response = await api.post('/services/app/Devices/AccessVersion', {
      deviceCode
    });
    return response.data;
  },

  // 获取设备策略
  getPolicy: async (deviceCode: string) => {
    const response = await api.post('/services/app/Devices/AccessJson', {
      deviceCode
    });
    return response.data;
  },
};

// 用户管理API
export const userAPI = {
  // 获取所有用户
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data.users;
  },

  // 创建用户
  createUser: async (userData: CreateUserRequest): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data.user;
  },

  // 更新用户
  updateUser: async (id: number, userData: UpdateUserRequest): Promise<User> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data.user;
  },

  // 删除用户
  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};

export default api;
